import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Search, MapPin } from 'lucide-react';

interface ProfessionalSearchBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  location: string;
  onLocationChange: (value: string) => void;
  radius: number;
  onRadiusChange: (value: number) => void;
  licenseVerifiedOnly: boolean;
  onLicenseVerifiedOnlyChange: (value: boolean) => void;
}

const radiusOptions = [
  { value: 5, label: '5 miles' },
  { value: 10, label: '10 miles' },
  { value: 25, label: '25 miles' },
  { value: 50, label: '50 miles' },
  { value: 100, label: '100 miles' }
];

export function ProfessionalSearchBar({
  search,
  onSearchChange,
  location,
  onLocationChange,
  radius,
  onRadiusChange,
  licenseVerifiedOnly,
  onLicenseVerifiedOnlyChange
}: ProfessionalSearchBarProps) {
  return (
    <div className="mb-8 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Name/Keyword Search */}
        <div className="md:col-span-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Name, specialty, or keyword"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 h-12"
          />
        </div>

        {/* Location Search */}
        <div className="md:col-span-3 relative">
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="City or ZIP"
            value={location}
            onChange={(e) => onLocationChange(e.target.value)}
            className="pl-10 h-12"
          />
        </div>

        {/* Radius Select */}
        <div className="md:col-span-2">
          <Select value={radius.toString()} onValueChange={(value) => onRadiusChange(Number(value))}>
            <SelectTrigger className="h-12">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {radiusOptions.map(option => (
                <SelectItem key={option.value} value={option.value.toString()}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* License Verified Toggle */}
        <div className="md:col-span-2 flex items-center space-x-2 h-12">
          <Switch
            id="license-verified"
            checked={licenseVerifiedOnly}
            onCheckedChange={onLicenseVerifiedOnlyChange}
          />
          <Label 
            htmlFor="license-verified" 
            className="text-sm font-medium cursor-pointer"
          >
            Licensed only
          </Label>
        </div>

        {/* Search Button */}
        <div className="md:col-span-1">
          <Button className="w-full h-12" size="lg">
            Search
          </Button>
        </div>
      </div>
    </div>
  );
}